/**
 * Server state for build step 8 — email reading (Clients → Emails). One hook per route in packages/contracts
 * routes.step8 / routes.step1:
 *   GET /email-threads (stats, my_gmail, members_not_connected, source_last_success_at; paged),
 *   PATCH /email-threads/{id} (assign to a client · "Not a task" with a reason),
 *   POST /email-threads/{id}/task (Make task), GET /proposed-items?thread_id, POST /proposed-items/{id}/confirm,
 *   POST /proposed-items/{id}/reject, POST /clients/{id}/email-domains (the domain offer after assigning, managers),
 *   GET /auth/google/connect (a browser navigation — it answers 302 to Google, so it is a link, not a fetch).
 *
 * Visibility, tone masking and `can_edit` are the server's; nothing here decides who sees what.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  buildPath,
  routes,
  type ConfirmProposedBody,
  type EmailThread,
  type EmailThreadPage,
  type EmailThreadState,
  type MakeThreadTaskBody,
  type ProposedItemPage,
  type RejectProposedBody,
  type RouteQuery,
  type UpdateEmailThreadBody,
  type UpdateEmailThreadResponse,
  type MadeFromEmail,
} from '@amplispot/contracts';
import { call, newIdempotencyKey } from '../api';
import { refreshAfterWrite } from './work';

/** The pills: All | Needs us | Waiting on client | Replied, plus the unmatched queue when there is one. */
export type MailFilter = 'all' | EmailThreadState | 'unmatched';

export const mkeys = {
  all: ['mail'] as const,
  threads: (f: MailFilter) => ['mail', 'threads', f] as const,
  proposals: (threadId: string) => ['mail', 'proposals', threadId] as const,
};

export function refreshMail(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: mkeys.all });
  refreshAfterWrite(qc);
}

const filterQuery = (f: MailFilter): RouteQuery<'emailThreads.list'> =>
  f === 'all' ? {} : f === 'unmatched' ? { unmatched: 'true' } : { state: f };

/**
 * GET /email-threads, page by page ("Show more" fetches one more page). The page extras (stats, my_gmail, …) are
 * the same on every page, so the first page's are used.
 */
export function useEmailThreads(f: MailFilter) {
  const query = useInfiniteQuery({
    queryKey: mkeys.threads(f),
    queryFn: ({ pageParam }): Promise<EmailThreadPage> =>
      call('emailThreads.list', { query: { ...filterQuery(f), limit: 50, cursor: pageParam } as RouteQuery<'emailThreads.list'> }) as Promise<EmailThreadPage>,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: (prev) => prev,
  });
  const pages = query.data?.pages ?? [];
  const threads: EmailThread[] = pages.flatMap((p) => p.data);
  return { ...query, page: pages[0] ?? null, threads, more: query.hasNextPage };
}

/**
 * GET /email-threads/{id} — the thread the drawer or a pop-up has open, read on its own only once it is no longer in
 * the loaded list (an Assign moves it off "Not matched"), so its version and client stay current. Lives under the
 * mail key, so every mail write refreshes it.
 */
export function useEmailThread(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['mail', 'thread', id ?? ''] as const,
    queryFn: (): Promise<EmailThread> => call('emailThreads.get', { params: { id: id! } }) as Promise<EmailThread>,
    enabled: !!id && enabled,
  });
}

/** GET /proposed-items?thread_id — what was found in one thread (pending first, then decided). */
export function useThreadProposals(threadId: string | null) {
  return useQuery({
    queryKey: mkeys.proposals(threadId ?? ''),
    queryFn: (): Promise<ProposedItemPage> => call('proposedItems.list', { query: { thread_id: threadId!, limit: 50 } }) as Promise<ProposedItemPage>,
    enabled: !!threadId,
  });
}

// ---------------------------------------------------------------- writes

function useMailWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => refreshMail(qc), onError: () => refreshMail(qc) });
}

/** PATCH /email-threads/{id} with the row version (If-Match). */
export function useUpdateThread() {
  return useMailWrite(
    (v: { thread: EmailThread; body: UpdateEmailThreadBody }): Promise<UpdateEmailThreadResponse> =>
      call('emailThreads.update', { params: { id: v.thread.id }, ifMatch: v.thread.version, body: v.body }) as Promise<UpdateEmailThreadResponse>,
  );
}

/** POST /email-threads/{id}/task. The key is made once per pop-up / click, so a retried click cannot make two. */
export function useMakeThreadTask() {
  return useMailWrite(
    (v: { id: string; body: MakeThreadTaskBody; key?: string }): Promise<MadeFromEmail> =>
      call('emailThreads.makeTask', { params: { id: v.id }, idempotencyKey: v.key ?? newIdempotencyKey(), body: v.body }) as Promise<MadeFromEmail>,
  );
}

export function useConfirmProposal() {
  return useMailWrite((v: { id: string; body: ConfirmProposedBody; key: string }) =>
    call('proposedItems.confirm', { params: { id: v.id }, idempotencyKey: v.key, body: v.body }),
  );
}

export function useRejectProposal() {
  return useMailWrite((v: { id: string; body: RejectProposedBody }) => call('proposedItems.reject', { params: { id: v.id }, body: v.body }));
}

/** POST /clients/{id}/email-domains, one per offered domain; reports which were added and which failed. */
export function useAddDomains() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { clientId: string; domains: string[] }) => {
      const added: string[] = [];
      // Each failure keeps its own error: a 409 names the client that has the domain, a 403/422/5xx says something else.
      const failed: { domain: string; error: unknown }[] = [];
      for (const domain of v.domains) {
        try {
          await call('emailDomains.create', { params: { id: v.clientId }, idempotencyKey: newIdempotencyKey(), body: { domain } });
          added.push(domain);
        } catch (error) {
          failed.push({ domain, error });
        }
      }
      return { added, failed };
    },
    onSettled: () => refreshMail(qc),
  });
}

/** GET /auth/google/connect is a redirect to Google: the page navigates there (after the read-only disclosure). */
export const gmailConnectHref = (redirect = '/clients/emails'): string =>
  `${buildPath(routes['google.connect'].path)}?${new URLSearchParams({ scope: 'gmail', redirect }).toString()}`;

// ---------------------------------------------------------------- display helpers

/** Prototype EMS: chip tone per state. */
export const STATE_CHIP: Record<EmailThreadState, string> = { waiting: 'warm', needs: 'cold', replied: 'hot' };

/** Prototype mailTone chip tones. */
export const TONE_CHIP: Record<string, string> = {
  leave: 'cold',
  escalate: 'cold',
  negative: 'warm',
  positive: 'hot',
  urgent: 'info',
  neutral: '',
  unknown: '',
};

/** The poll runs every 10 minutes; ADR §12 shows the banner when the last success is older than twice that. */
export const MAIL_STALE_MS = 20 * 60 * 1000;

const istDate = (ts: string) => new Date(ts).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Prototype `m.when`: the IST day, "1 Oct" (the drawer adds the time). */
export function whenLabel(ts: string, _today?: string): string {
  const [, m, day] = istDate(ts).split('-').map(Number);
  return `${day} ${MON[(m ?? 1) - 1]}`;
}

/** "1 Oct, 4:12 pm" (IST) — the drawer's "From the client, …". */
export const whenWithTime = (ts: string): string =>
  `${whenLabel(ts)}, ${new Date(ts).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase()}`;

/** "12 min ago" / "3 h ago" / "2 days ago" — for the staleness banner. */
export function agoLabel(ts: string, now = Date.now()): string {
  const min = Math.max(0, Math.round((now - new Date(ts).getTime()) / 60000));
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} days ago`;
}
